import { useEffect, useMemo, useState } from 'react';
import { adminApi } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Loader2, Plus, Play, CheckCircle2, Ban, Pencil, Wand2, Trash2, Truck } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const normalizeList = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  return [];
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const statusBadge = (status) => {
  const map = {
    draft: 'bg-gray-100 text-gray-700',
    planned: 'bg-amber-100 text-amber-700',
    in_progress: 'bg-blue-100 text-blue-700',
    completed: 'bg-green-100 text-green-700',
    cancelled: 'bg-rose-100 text-rose-700',
  };
  return map[status] || 'bg-gray-100 text-gray-700';
};

const blankRawRow = () => ({ raw_item_id: '', kg_required: '', note: '' });
const blankOutputRow = () => ({ variant_id: '', qty_required: '', blend_item_id: '', note: '' });
const blankPackingMaterialRow = () => ({ packing_material_item_id: '', qty_required: '', note: '' });
const blankRawWastageRow = () => ({ raw_item_id: '', wastage_kg: '', note: '' });
const blankMaterialWastageRow = () => ({ packing_material_item_id: '', wastage_qty: '', note: '' });

const aggregateFinishedOutputsFromRequests = (requests) => {
  const aggregated = new Map();
  (requests || []).forEach((request) => {
    (request.items || []).forEach((item) => {
      const variantId = String(item.variant_id || '').trim();
      const qtyRequired = Number(item.shortfall ?? item.transfer_qty ?? item.required_qty ?? 0);
      if (!variantId || qtyRequired <= 0) return;
      const existing = aggregated.get(variantId) || { variant_id: variantId, qty_required: 0, note: '' };
      existing.qty_required += qtyRequired;
      aggregated.set(variantId, existing);
    });
  });
  return Array.from(aggregated.values()).map((item) => ({
    variant_id: item.variant_id,
    qty_required: String(item.qty_required),
    note: '',
  }));
};

export default function PackingSessionsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [staff, setStaff] = useState([]);
  const [rawItems, setRawItems] = useState([]);
  const [blendedStockLots, setBlendedStockLots] = useState([]);
  const [packingMaterialItems, setPackingMaterialItems] = useState([]);
  const [variants, setVariants] = useState([]);
  const [packingRequests, setPackingRequests] = useState([]);

  const [filters, setFilters] = useState({
    status: 'all',
    search: '',
    date_from: '',
    date_to: '',
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [form, setForm] = useState({
    session_name: '',
    creation_mode: 'finished_first',
    packing_request_ids: [],
    source_warehouse_id: '',
    destination_warehouse_id: '',
    assigned_staff_ids: [''],
    planned_raw_inputs: [blankRawRow()],
    planned_packing_material_inputs: [blankPackingMaterialRow()],
    planned_finished_outputs: [blankOutputRow()],
    notes: '',
  });

  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [completeForm, setCompleteForm] = useState({
    production_loss_kg: '',
    notes: '',
    actual_raw_inputs: [blankRawRow()],
    actual_packing_material_inputs: [blankPackingMaterialRow()],
    actual_finished_outputs: [blankOutputRow()],
    raw_wastage_inputs: [blankRawWastageRow()],
    packing_material_wastage_inputs: [blankMaterialWastageRow()],
  });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [actionLoading, setActionLoading] = useState({ sessionId: null, action: null });

  const activePackingRequests = useMemo(
    () => packingRequests.filter((request) => {
      const status = String(request.status || '').toLowerCase();
      return status === 'pending' || status === 'in_progress';
    }),
    [packingRequests]
  );
  const variantMap = useMemo(
    () => Object.fromEntries((variants || []).map((variant) => [variant.id, variant])),
    [variants]
  );
  const availableBlendItems = useMemo(() => {
    const aggregated = {};
    (blendedStockLots || []).forEach((lot) => {
      const itemId = String(lot.blend_item_id || '').trim();
      if (!itemId) return;
      if (!aggregated[itemId]) {
        aggregated[itemId] = {
          blend_item_id: itemId,
          blend_item_name: lot.blend_item_name || 'Bulk Blend',
          kg_total: 0,
          warehouse_name: lot.warehouse_name || '',
        };
      }
      aggregated[itemId].kg_total += Number(lot.kg_total || 0);
    });
    return Object.values(aggregated).sort((a, b) => String(a.blend_item_name || '').localeCompare(String(b.blend_item_name || '')));
  }, [blendedStockLots]);

  const getSessionStepState = (session) => {
    const prepared = String(session.stock_preparation_status || 'not_prepared') === 'prepared';
    const completed = session.status === 'completed';
    const started = session.status === 'in_progress' || completed;
    return [
      { label: 'Plan', done: true, current: !prepared && ['draft', 'planned'].includes(session.status) },
      { label: 'Move Stock', done: prepared, current: !prepared && ['draft', 'planned'].includes(session.status) },
      { label: 'Start', done: started, current: prepared && ['draft', 'planned'].includes(session.status) },
      { label: 'Complete', done: completed, current: started && !completed },
    ];
  };

  const loadLookups = async () => {
    const [packingMaterialsRes, warehousesRes, staffRes, rawItemsRes, blendedLotsRes, variantsRes, requestsRes] = await Promise.all([
      adminApi.getPackingMaterialItems(),
      adminApi.getWarehouses(),
      adminApi.getSalesStaff(),
      adminApi.getRawItems(),
      adminApi.getBlendedStockLots({}),
      adminApi.getProductVariants(),
      adminApi.getPackingRequests({ page_size: 500 }),
    ]);
    setPackingMaterialItems(packingMaterialsRes.data || []);
    setWarehouses(warehousesRes.data || []);
    setStaff(staffRes.data || []);
    setRawItems(rawItemsRes.data || []);
    setBlendedStockLots(blendedLotsRes.data || []);
    setVariants(normalizeList(variantsRes.data));
    setPackingRequests(normalizeList(requestsRes.data));
  };

  const loadSessions = async () => {
    const response = await adminApi.getPackingSessions(filters);
    setSessions(response.data?.items || []);
  };

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([loadLookups(), loadSessions()]);
    } catch (error) {
      toast.error('Failed to load packing sessions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadSessions().catch(() => toast.error('Failed to load packing sessions'));
  }, [filters]);

  const resetForm = () => {
    const salesWarehouse = warehouses.find((warehouse) => warehouse.is_default_sales) || warehouses[0];
    const packingWarehouse =
      warehouses.find((warehouse) => warehouse.warehouse_type === 'PACKING' || warehouse.tag === 'PACKING') ||
      warehouses.find((warehouse) => String(warehouse.name || '').toLowerCase().includes('pack')) ||
      salesWarehouse ||
      null;

    setForm({
      session_name: '',
      creation_mode: 'finished_first',
      packing_request_ids: [],
      source_warehouse_id: packingWarehouse?.id || '',
      destination_warehouse_id: salesWarehouse?.id || '',
      assigned_staff_ids: [''],
      planned_raw_inputs: [blankRawRow()],
      planned_packing_material_inputs: [blankPackingMaterialRow()],
      planned_finished_outputs: [blankOutputRow()],
      notes: '',
    });
    setEditingSession(null);
  };

  const openCreateModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const openEditModal = (session) => {
    setEditingSession(session);
    setForm({
      session_name: session.session_name || '',
      creation_mode: session.creation_mode || 'finished_first',
      packing_request_ids: session.packing_request_ids || (session.packing_request_id ? [session.packing_request_id] : []),
      source_warehouse_id: session.source_warehouse_id || '',
      destination_warehouse_id: session.destination_warehouse_id || '',
      assigned_staff_ids: (session.assigned_staff || []).map((item) => item.staff_id).concat((session.assigned_staff || []).length ? [] : ['']),
      planned_raw_inputs: (session.planned_raw_inputs || []).map((item) => ({
        raw_item_id: item.raw_item_id || '',
        kg_required: String(item.kg_required ?? ''),
        note: item.note || '',
      })).concat((session.planned_raw_inputs || []).length ? [] : [blankRawRow()]),
      planned_packing_material_inputs: (session.planned_packing_material_inputs || []).map((item) => ({
        packing_material_item_id: item.packing_material_item_id || '',
        qty_required: String(item.qty_required ?? ''),
        note: item.note || '',
      })).concat((session.planned_packing_material_inputs || []).length ? [] : [blankPackingMaterialRow()]),
      planned_finished_outputs: (session.planned_finished_outputs || []).map((item) => ({
        variant_id: item.variant_id || '',
        qty_required: String(item.qty_required ?? ''),
        blend_item_id: item.blend_item_id || '',
        note: item.note || '',
      })).concat((session.planned_finished_outputs || []).length ? [] : [blankOutputRow()]),
      notes: session.notes || '',
    });
    setModalOpen(true);
  };

  const buildPayload = () => {
    const assigned_staff = form.assigned_staff_ids.filter(Boolean).map((staff_id) => ({ staff_id }));
    const planned_raw_inputs = form.planned_raw_inputs
      .filter((item) => item.raw_item_id && item.kg_required)
      .map((item) => ({
        raw_item_id: item.raw_item_id,
        kg_required: Number(item.kg_required),
        note: item.note || '',
      }));
    const planned_finished_outputs = form.planned_finished_outputs
      .filter((item) => item.variant_id && item.qty_required)
      .map((item) => ({
        variant_id: item.variant_id,
        qty_required: Number(item.qty_required),
        note: item.note || '',
      }));
    const planned_packing_material_inputs = form.planned_packing_material_inputs
      .filter((item) => item.packing_material_item_id && item.qty_required)
      .map((item) => ({
        packing_material_item_id: item.packing_material_item_id,
        qty_required: Number(item.qty_required),
        note: item.note || '',
      }));

    return {
      session_name: form.session_name,
      creation_mode: form.creation_mode,
      packing_request_ids: form.packing_request_ids,
      source_warehouse_id: form.source_warehouse_id || null,
      destination_warehouse_id: form.destination_warehouse_id || null,
      assigned_staff,
      planned_raw_inputs,
      planned_packing_material_inputs,
      planned_finished_outputs,
      notes: form.notes,
    };
  };

  const handleSave = async () => {
    const payload = buildPayload();
    if (payload.assigned_staff.length === 0) {
      toast.error('Assign at least one staff member');
      return;
    }
    if (payload.planned_finished_outputs.length === 0) {
      toast.error('Add at least one planned finished output');
      return;
    }

    setSaving(true);
    try {
      if (editingSession) {
        await adminApi.updatePackingSession(editingSession.id, payload);
        toast.success('Packing session updated');
      } else {
        await adminApi.createPackingSession(payload);
        toast.success('Packing session created');
      }
      setModalOpen(false);
      await loadSessions();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to save packing session');
    } finally {
      setSaving(false);
    }
  };

  const handleSuggestRaw = async () => {
    const planned_finished_outputs = form.planned_finished_outputs
      .filter((item) => item.variant_id && item.qty_required)
      .map((item) => ({ variant_id: item.variant_id, qty_required: Number(item.qty_required) }));
    if (planned_finished_outputs.length === 0) {
      toast.error('Add finished outputs first');
      return;
    }
    try {
      const response = await adminApi.suggestPackingSessionRaw({ planned_finished_outputs });
      const items = response.data?.items || [];
      setForm((prev) => ({
        ...prev,
        planned_raw_inputs: items.length
          ? items.map((item) => ({ raw_item_id: item.raw_item_id, kg_required: String(item.kg_required), note: '' }))
          : [blankRawRow()],
      }));
      toast.success('Raw requirement suggested');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to suggest raw requirement');
    }
  };

  const handleLoadRecipeMix = async () => {
    await handleSuggestRaw();
  };

  const handlePackingRequestToggle = (requestId, checked) => {
    const nextRequestIds = checked
      ? Array.from(new Set([...form.packing_request_ids, requestId]))
      : form.packing_request_ids.filter((id) => id !== requestId);
    const selectedRequests = activePackingRequests.filter((item) => nextRequestIds.includes(item.id));
    setForm((prev) => ({
      ...prev,
      packing_request_ids: nextRequestIds,
      planned_finished_outputs: selectedRequests.length ? aggregateFinishedOutputsFromRequests(selectedRequests) : [blankOutputRow()],
    }));
  };

  const handleSessionAction = async (session, action) => {
    try {
      if (action === 'start') {
        setActionLoading({ sessionId: session.id, action });
        await adminApi.startPackingSession(session.id);
        toast.success('Packing session started');
      } else if (action === 'cancel') {
        const confirmCancel = window.confirm('Cancel this packing session?');
        if (!confirmCancel) return;
        setActionLoading({ sessionId: session.id, action });
        await adminApi.cancelPackingSession(session.id, {});
        toast.success('Packing session cancelled');
      } else if (action === 'prepare') {
        setActionLoading({ sessionId: session.id, action });
        const preview = await adminApi.previewPackingSessionStockPreparation(session.id);
        if (!preview.data?.ready) {
          const shortageText = (preview.data?.shortages || [])
            .map((item) => item.raw_item_name ? `${item.raw_item_name}: ${item.shortage_kg} kg short` : `${item.packing_material_item_name}: ${item.shortage_qty} ${item.unit || ''} short`)
            .join('; ');
          toast.error(shortageText || 'Stock preparation has shortages');
          return;
        }
        await adminApi.confirmPackingSessionStockPreparation(session.id);
        toast.success('Required stock moved to Packing');
      } else if (action === 'complete') {
        setSelectedSession(session);
        setCompleteForm({
          production_loss_kg: String(session.production_loss_kg || ''),
          notes: session.notes || '',
          actual_raw_inputs: (session.actual_raw_inputs || session.planned_raw_inputs || []).length
            ? (session.actual_raw_inputs || session.planned_raw_inputs || []).map((item) => ({
                raw_item_id: item.raw_item_id || '',
                kg_required: String(item.kg_required ?? ''),
                note: item.note || '',
              }))
            : [blankRawRow()],
          actual_packing_material_inputs: (session.actual_packing_material_inputs || session.planned_packing_material_inputs || []).length
            ? (session.actual_packing_material_inputs || session.planned_packing_material_inputs || []).map((item) => ({
                packing_material_item_id: item.packing_material_item_id || '',
                qty_required: String(item.qty_required ?? ''),
                note: item.note || '',
              }))
            : [blankPackingMaterialRow()],
          actual_finished_outputs: (session.actual_finished_outputs || session.planned_finished_outputs || []).length
            ? (session.actual_finished_outputs || session.planned_finished_outputs || []).map((item) => ({
                variant_id: item.variant_id || '',
                qty_required: String(item.qty_required ?? ''),
                blend_item_id: item.blend_item_id || '',
                note: item.note || '',
              }))
            : [blankOutputRow()],
          raw_wastage_inputs: (session.planned_raw_inputs || []).length
            ? (session.planned_raw_inputs || []).map((item) => ({ raw_item_id: item.raw_item_id || '', wastage_kg: '', note: '' }))
            : [blankRawWastageRow()],
          packing_material_wastage_inputs: (session.planned_packing_material_inputs || []).length
            ? (session.planned_packing_material_inputs || []).map((item) => ({ packing_material_item_id: item.packing_material_item_id || '', wastage_qty: '', note: '' }))
            : [blankMaterialWastageRow()],
        });
        setCompleteModalOpen(true);
        return;
      }
      await loadSessions();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update packing session');
    } finally {
      setActionLoading({ sessionId: null, action: null });
    }
  };

  const handleDownloadStickers = async (session) => {
    setActionLoading({ sessionId: session.id, action: 'download' });
    try {
      const response = await adminApi.downloadPackingSessionStickers(session.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${session.batch_code || session.production_batch_ref || session.session_ref || 'stickers'}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Sticker PDF downloaded');
      await loadSessions();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to download sticker PDF');
    } finally {
      setActionLoading({ sessionId: null, action: null });
    }
  };

  const handlePreviewStickers = async (session) => {
    setActionLoading({ sessionId: session.id, action: 'preview' });
    setPreviewLoading(true);
    try {
      const response = await adminApi.previewPackingSessionStickers(session.id);
      setPreviewData(response.data);
      setPreviewOpen(true);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to load sticker preview');
    } finally {
      setPreviewLoading(false);
      setActionLoading({ sessionId: null, action: null });
    }
  };

  const handleComplete = async () => {
    if (!selectedSession) return;
    setSaving(true);
    setActionLoading({ sessionId: selectedSession.id, action: 'complete' });
    try {
      await adminApi.completePackingSession(selectedSession.id, {
        production_loss_kg: Number(completeForm.production_loss_kg || 0),
        notes: completeForm.notes || '',
        actual_raw_inputs: (completeForm.actual_raw_inputs || [])
          .filter((item) => item.raw_item_id && item.kg_required)
          .map((item) => ({
            raw_item_id: item.raw_item_id,
            kg_required: Number(item.kg_required),
            note: item.note || '',
          })),
        actual_packing_material_inputs: (completeForm.actual_packing_material_inputs || [])
          .filter((item) => item.packing_material_item_id && item.qty_required)
          .map((item) => ({
            packing_material_item_id: item.packing_material_item_id,
            qty_required: Number(item.qty_required),
            note: item.note || '',
          })),
        actual_finished_outputs: (completeForm.actual_finished_outputs || [])
          .filter((item) => item.variant_id && item.qty_required)
          .map((item) => ({
            variant_id: item.variant_id,
            qty_required: Number(item.qty_required),
            blend_item_id: item.blend_item_id || null,
            note: item.note || '',
          })),
        raw_wastage_inputs: (completeForm.raw_wastage_inputs || [])
          .filter((item) => item.raw_item_id && item.wastage_kg)
          .map((item) => ({
            raw_item_id: item.raw_item_id,
            wastage_kg: Number(item.wastage_kg),
            note: item.note || '',
          })),
        packing_material_wastage_inputs: (completeForm.packing_material_wastage_inputs || [])
          .filter((item) => item.packing_material_item_id && item.wastage_qty)
          .map((item) => ({
            packing_material_item_id: item.packing_material_item_id,
            wastage_qty: Number(item.wastage_qty),
            note: item.note || '',
          })),
      });
      toast.success('Packing session completed');
      setCompleteModalOpen(false);
      setSelectedSession(null);
      await loadSessions();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to complete packing session');
    } finally {
      setSaving(false);
      setActionLoading({ sessionId: null, action: null });
    }
  };

  const updateRawRow = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      planned_raw_inputs: prev.planned_raw_inputs.map((row, rowIndex) => (rowIndex === index ? { ...row, [field]: value } : row)),
    }));
  };

  const updateOutputRow = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      planned_finished_outputs: prev.planned_finished_outputs.map((row, rowIndex) => (rowIndex === index ? { ...row, [field]: value } : row)),
    }));
  };

  const updatePackingMaterialRow = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      planned_packing_material_inputs: prev.planned_packing_material_inputs.map((row, rowIndex) => (rowIndex === index ? { ...row, [field]: value } : row)),
    }));
  };

  const updateStaffRow = (index, value) => {
    setForm((prev) => ({
      ...prev,
      assigned_staff_ids: prev.assigned_staff_ids.map((staffId, rowIndex) => (rowIndex === index ? value : staffId)),
    }));
  };

  const updateCompleteRawRow = (index, field, value) => {
    setCompleteForm((prev) => ({
      ...prev,
      actual_raw_inputs: prev.actual_raw_inputs.map((row, rowIndex) => (rowIndex === index ? { ...row, [field]: value } : row)),
    }));
  };

  const updateCompleteMaterialRow = (index, field, value) => {
    setCompleteForm((prev) => ({
      ...prev,
      actual_packing_material_inputs: prev.actual_packing_material_inputs.map((row, rowIndex) => (rowIndex === index ? { ...row, [field]: value } : row)),
    }));
  };

  const updateRawWastageRow = (index, field, value) => {
    setCompleteForm((prev) => ({
      ...prev,
      raw_wastage_inputs: prev.raw_wastage_inputs.map((row, rowIndex) => (rowIndex === index ? { ...row, [field]: value } : row)),
    }));
  };

  const updateMaterialWastageRow = (index, field, value) => {
    setCompleteForm((prev) => ({
      ...prev,
      packing_material_wastage_inputs: prev.packing_material_wastage_inputs.map((row, rowIndex) => (rowIndex === index ? { ...row, [field]: value } : row)),
    }));
  };

  const updateCompleteOutputRow = (index, field, value) => {
    setCompleteForm((prev) => ({
      ...prev,
      actual_finished_outputs: prev.actual_finished_outputs.map((row, rowIndex) => (rowIndex === index ? { ...row, [field]: value } : row)),
    }));
  };

  return (
    <div className="space-y-6" data-testid="packing-sessions-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#2D2420]">Packing Sessions</h1>
          <p className="text-[#A8A8A8] mt-1">Plan packing work with staff, raw inputs, and expected finished output</p>
        </div>
        <Button onClick={openCreateModal} className="bg-[#C97A5E] hover:bg-[#B06850]">
          <Plus className="w-4 h-4 mr-2" />
          Create Session
        </Button>
      </div>

      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label>Status</Label>
              <Select value={filters.status} onValueChange={(value) => setFilters((prev) => ({ ...prev, status: value }))}>
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="planned">Planned</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Search</Label>
              <Input value={filters.search} onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))} className="border-[#E5E5E5]" />
            </div>
            <div>
              <Label>Date From</Label>
              <Input type="date" value={filters.date_from} onChange={(e) => setFilters((prev) => ({ ...prev, date_from: e.target.value }))} className="border-[#E5E5E5]" />
            </div>
            <div>
              <Label>Date To</Label>
              <Input type="date" value={filters.date_to} onChange={(e) => setFilters((prev) => ({ ...prev, date_to: e.target.value }))} className="border-[#E5E5E5]" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-[#2D2420]">Bulk Blend Stock in Packing Warehouse</h2>
              <p className="text-sm text-[#A8A8A8]">These blended items can be chosen during packing completion.</p>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            {availableBlendItems.length === 0 ? (
              <div className="text-sm text-[#A8A8A8]">No blended bulk stock available.</div>
            ) : availableBlendItems.map((item) => (
              <div key={item.blend_item_id} className="rounded-lg border border-[#E5E5E5] px-4 py-3">
                <div className="font-medium text-[#2D2420]">{item.blend_item_name}</div>
                <div className="text-xs text-[#A8A8A8]">{item.warehouse_name || 'Packing Warehouse'}</div>
                <div className="mt-1 text-sm text-[#5C4033]">{Number(item.kg_total || 0).toFixed(3)} kg available</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-[#C97A5E]" />
            </div>
          ) : sessions.length === 0 ? (
            <div className="flex items-center justify-center h-64 text-[#A8A8A8]">No packing sessions</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Session</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Mode</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Staff</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Planned Output</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Status</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Created</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E5E5]">
                  {sessions.map((session) => (
                    <tr key={session.id} className="hover:bg-[#FAFAF9]/60 align-top">
                      <td className="py-3 px-4">
                        <div className="font-medium text-[#2D2420]">{session.session_ref}</div>
                        <div className="text-sm text-[#A8A8A8]">{session.session_name || session.order_ref || '-'}</div>
                        {session.packing_request_refs?.length ? (
                          <div className="text-xs text-[#A8A8A8]">
                            Requests: {session.packing_request_refs.slice(0, 2).join(', ')}
                            {session.packing_request_refs.length > 2 ? ` +${session.packing_request_refs.length - 2} more` : ''}
                          </div>
                        ) : null}
                        {session.batch_code ? (
                          <div className="text-xs text-[#5C4033]">Unified Batch: {session.batch_code}</div>
                        ) : null}
                        {session.production_batch_ref ? (
                          <div className="text-xs text-[#5C4033]">Batch: {session.production_batch_ref}</div>
                        ) : null}
                        {session.blended_output_count > 0 ? (
                          <div className="text-xs text-amber-700">Blended outputs: {session.blended_output_count}</div>
                        ) : null}
                        {session.sticker_summary?.total_stickers ? (
                          <div className="text-xs text-[#A8A8A8]">Stickers: {session.sticker_summary.total_stickers}</div>
                        ) : null}
                        <div className="mt-3 flex flex-wrap gap-1">
                          {getSessionStepState(session).map((step, index) => (
                            <span
                              key={`${session.id}-step-${step.label}`}
                              className={cn(
                                'rounded-full px-2 py-1 text-[11px] font-medium',
                                step.done ? 'bg-emerald-100 text-emerald-700' : step.current ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'
                              )}
                            >
                              {index + 1}. {step.label}
                            </span>
                          ))}
                        </div>
                        {String(session.stock_preparation_status || 'not_prepared') === 'prepared' ? (
                          <div className="mt-2 text-xs text-emerald-700">Stock prepared: raw + packing materials moved to Packing</div>
                        ) : ['draft', 'planned'].includes(session.status) ? (
                          <div className="mt-2 text-xs text-amber-700">Next: move required stock from Main to Packing</div>
                        ) : null}
                      </td>
                      <td className="py-3 px-4 text-[#2D2420] capitalize">{String(session.creation_mode || '').replace('_', ' ')}</td>
                      <td className="py-3 px-4 text-[#2D2420]">
                        {(session.assigned_staff || []).map((item) => item.staff_name).filter(Boolean).join(', ') || '-'}
                      </td>
                      <td className="py-3 px-4 text-sm text-[#2D2420]">
                        {(session.planned_finished_outputs || []).slice(0, 2).map((item) => (
                          <div key={`${session.id}-${item.variant_id}`}>
                            {item.variant_name || item.sku || item.variant_id} • {item.qty_required}
                            {item.is_blended ? <span className="ml-2 text-xs text-amber-700">Blended</span> : null}
                          </div>
                        ))}
                        {(session.planned_finished_outputs || []).length > 2 ? (
                          <div className="text-[#A8A8A8]">+{session.planned_finished_outputs.length - 2} more</div>
                        ) : null}
                      </td>
                      <td className="py-3 px-4">
                        <Badge className={cn('text-xs', statusBadge(session.status))}>{session.status}</Badge>
                      </td>
                      <td className="py-3 px-4 text-[#A8A8A8]">{formatDateTime(session.created_at)}</td>
                      <td className="py-3 px-4">
                        {actionLoading.sessionId === session.id ? (
                          <div className="mb-2 flex justify-end">
                            <span className="text-xs text-[#A8A8A8] capitalize">
                              {String(actionLoading.action || 'processing').replace('_', ' ')}...
                            </span>
                          </div>
                        ) : null}
                        <TooltipProvider>
                        <div className="relative flex justify-end gap-2 min-h-9">
                          {actionLoading.sessionId === session.id ? (
                            <div className="absolute inset-0 z-10 flex items-center justify-end rounded-md bg-white/70 backdrop-blur-[1px]">
                              <Loader2 className="w-4 h-4 animate-spin text-[#C97A5E]" />
                            </div>
                          ) : null}
                          {['draft', 'planned'].includes(session.status) ? (
                            <>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button type="button" size="icon" variant="ghost" className="text-[#5C4033]" onClick={() => openEditModal(session)} disabled={actionLoading.sessionId === session.id}>
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Edit Session</TooltipContent>
                              </Tooltip>
                              {String(session.stock_preparation_status || 'not_prepared') !== 'prepared' ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button type="button" size="icon" variant="ghost" className="text-amber-700" onClick={() => handleSessionAction(session, 'prepare')} disabled={actionLoading.sessionId === session.id}>
                                      <Truck className="w-4 h-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Move required stock to Packing</TooltipContent>
                                </Tooltip>
                              ) : null}
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button type="button" size="icon" variant="ghost" className="text-blue-700" onClick={() => handleSessionAction(session, 'start')} disabled={actionLoading.sessionId === session.id || String(session.stock_preparation_status || 'not_prepared') !== 'prepared'}>
                                    <Play className="w-4 h-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Start Session</TooltipContent>
                              </Tooltip>
                            </>
                          ) : null}
                          {['draft', 'planned', 'in_progress'].includes(session.status) ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button type="button" size="icon" variant="ghost" className="text-green-700" onClick={() => handleSessionAction(session, 'complete')} disabled={actionLoading.sessionId === session.id}>
                                  <CheckCircle2 className="w-4 h-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Complete Session</TooltipContent>
                            </Tooltip>
                          ) : null}
                          {session.status !== 'completed' && session.status !== 'cancelled' ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button type="button" size="icon" variant="ghost" className="text-rose-700" onClick={() => handleSessionAction(session, 'cancel')} disabled={actionLoading.sessionId === session.id}>
                                  <Ban className="w-4 h-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Cancel Session</TooltipContent>
                            </Tooltip>
                          ) : null}
                          {session.status === 'completed' ? (
                            <>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    className="border-[#E5E5E5] text-[#5C4033]"
                                    onClick={() => handlePreviewStickers(session)}
                                    disabled={actionLoading.sessionId === session.id}
                                  >
                                    Preview Stickers
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Preview Rendered Stickers</TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    className="border-[#E5E5E5] text-[#5C4033]"
                                    onClick={() => handleDownloadStickers(session)}
                                    disabled={actionLoading.sessionId === session.id}
                                  >
                                    Download Stickers
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Download 9-Up A4 Sticker PDF</TooltipContent>
                              </Tooltip>
                            </>
                          ) : null}
                        </div>
                        </TooltipProvider>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">{editingSession ? 'Edit Packing Session' : 'Create Packing Session'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Session Name</Label>
                <Input value={form.session_name} onChange={(e) => setForm((prev) => ({ ...prev, session_name: e.target.value }))} className="border-[#E5E5E5]" />
              </div>
              <div>
                <Label>Creation Mode</Label>
                <Select value={form.creation_mode} onValueChange={(value) => setForm((prev) => ({ ...prev, creation_mode: value }))}>
                  <SelectTrigger className="border-[#E5E5E5]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="raw_first">Raw First</SelectItem>
                    <SelectItem value="finished_first">Finished First</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Linked Packing Requests</Label>
                <div className="max-h-40 overflow-y-auto rounded-md border border-[#E5E5E5] p-3 space-y-2">
                  {activePackingRequests.length === 0 ? (
                    <div className="text-sm text-[#A8A8A8]">No pending packing requests available</div>
                  ) : activePackingRequests.map((request) => {
                    const checked = form.packing_request_ids.includes(request.id);
                    const disabled = String(request.status || '').toLowerCase() === 'in_progress' && !checked;
                    return (
                      <label key={request.id} className={cn('flex items-start gap-2 text-sm', disabled ? 'opacity-50' : 'cursor-pointer')}>
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={disabled}
                          onChange={(e) => handlePackingRequestToggle(request.id, e.target.checked)}
                          className="mt-1 h-4 w-4 rounded border-[#CFCFCF]"
                        />
                        <span className="text-[#2D2420]">
                          {request.order_ref || '-'} • {request.customer_name || '-'}
                          <span className="block text-xs text-[#A8A8A8]">{request.status === 'in_progress' ? `Linked to ${request.packing_session_ref || 'another session'}` : 'Pending request'}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label>Source Warehouse</Label>
                <Select value={form.source_warehouse_id} onValueChange={(value) => setForm((prev) => ({ ...prev, source_warehouse_id: value }))}>
                  <SelectTrigger className="border-[#E5E5E5]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {warehouses.map((warehouse) => (
                      <SelectItem key={warehouse.id} value={warehouse.id}>{warehouse.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Destination Warehouse</Label>
                <Select value={form.destination_warehouse_id} onValueChange={(value) => setForm((prev) => ({ ...prev, destination_warehouse_id: value }))}>
                  <SelectTrigger className="border-[#E5E5E5]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {warehouses.map((warehouse) => (
                      <SelectItem key={warehouse.id} value={warehouse.id}>{warehouse.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Assigned Staff</Label>
                <Button type="button" size="sm" variant="outline" className="border-[#E5E5E5]" onClick={() => setForm((prev) => ({ ...prev, assigned_staff_ids: [...prev.assigned_staff_ids, ''] }))}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Staff
                </Button>
              </div>
              {form.assigned_staff_ids.map((staffId, index) => (
                <div key={`staff-${index}`} className="flex gap-2">
                  <Select value={staffId || undefined} onValueChange={(value) => updateStaffRow(index, value)}>
                    <SelectTrigger className="border-[#E5E5E5]">
                      <SelectValue placeholder="Select staff" />
                    </SelectTrigger>
                    <SelectContent>
                      {staff.map((member) => (
                        <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="text-rose-700"
                    onClick={() => setForm((prev) => ({ ...prev, assigned_staff_ids: prev.assigned_staff_ids.filter((_, rowIndex) => rowIndex !== index) || [''] }))}
                    disabled={form.assigned_staff_ids.length === 1}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Planned Finished Outputs</Label>
                <div className="flex gap-2">
                  <Button type="button" size="sm" variant="outline" className="border-[#E5E5E5]" onClick={() => setForm((prev) => ({ ...prev, planned_finished_outputs: [...prev.planned_finished_outputs, blankOutputRow()] }))}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add Output
                  </Button>
                  <Button type="button" size="sm" variant="outline" className="border-[#E5E5E5]" onClick={handleSuggestRaw}>
                    <Wand2 className="w-4 h-4 mr-1" />
                    Suggest Raw
                  </Button>
                  <Button type="button" size="sm" variant="outline" className="border-[#E5E5E5]" onClick={handleLoadRecipeMix}>
                    <Wand2 className="w-4 h-4 mr-1" />
                    Load Recipe Mix
                  </Button>
                </div>
              </div>
              {form.planned_finished_outputs.map((row, index) => (
                <div key={`output-${index}`} className="grid grid-cols-1 md:grid-cols-[1.5fr_0.8fr_1fr_auto] gap-2">
                  <Select value={row.variant_id || undefined} onValueChange={(value) => updateOutputRow(index, 'variant_id', value)}>
                    <SelectTrigger className="border-[#E5E5E5]">
                      <SelectValue placeholder="Select variant" />
                    </SelectTrigger>
                    <SelectContent>
                      {variants.map((variant) => (
                        <SelectItem key={variant.id} value={variant.id}>
                          {variant.sku} {variant.master_name ? `• ${variant.master_name}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input value={row.qty_required} onChange={(e) => updateOutputRow(index, 'qty_required', e.target.value)} placeholder="Qty" type="number" className="border-[#E5E5E5]" />
                  <Input value={row.note} onChange={(e) => updateOutputRow(index, 'note', e.target.value)} placeholder="Note" className="border-[#E5E5E5]" />
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="text-rose-700"
                    onClick={() => setForm((prev) => ({ ...prev, planned_finished_outputs: prev.planned_finished_outputs.filter((_, rowIndex) => rowIndex !== index) || [blankOutputRow()] }))}
                    disabled={form.planned_finished_outputs.length === 1}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              {form.planned_finished_outputs.some((row) => variantMap[row.variant_id]?.is_blended) ? (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  Blended variants in this session will preload their saved raw mix recipe. You can still adjust planned raw inputs manually.
                </div>
              ) : null}
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Planned Raw Inputs</Label>
                <Button type="button" size="sm" variant="outline" className="border-[#E5E5E5]" onClick={() => setForm((prev) => ({ ...prev, planned_raw_inputs: [...prev.planned_raw_inputs, blankRawRow()] }))}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Raw
                </Button>
              </div>
              {form.planned_raw_inputs.map((row, index) => (
                <div key={`raw-${index}`} className="grid grid-cols-1 md:grid-cols-[1.5fr_0.8fr_1fr_auto] gap-2">
                  <Select value={row.raw_item_id || undefined} onValueChange={(value) => updateRawRow(index, 'raw_item_id', value)}>
                    <SelectTrigger className="border-[#E5E5E5]">
                      <SelectValue placeholder="Select raw item" />
                    </SelectTrigger>
                    <SelectContent>
                      {rawItems.map((item) => (
                        <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input value={row.kg_required} onChange={(e) => updateRawRow(index, 'kg_required', e.target.value)} placeholder="KG" type="number" className="border-[#E5E5E5]" />
                  <Input value={row.note} onChange={(e) => updateRawRow(index, 'note', e.target.value)} placeholder="Note" className="border-[#E5E5E5]" />
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="text-rose-700"
                    onClick={() => setForm((prev) => ({ ...prev, planned_raw_inputs: prev.planned_raw_inputs.filter((_, rowIndex) => rowIndex !== index) || [blankRawRow()] }))}
                    disabled={form.planned_raw_inputs.length === 1}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Planned Packing Materials</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="border-[#E5E5E5]"
                  onClick={() => setForm((prev) => ({ ...prev, planned_packing_material_inputs: [...prev.planned_packing_material_inputs, blankPackingMaterialRow()] }))}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Material
                </Button>
              </div>
              {form.planned_packing_material_inputs.map((row, index) => (
                <div key={`packing-material-${index}`} className="grid grid-cols-1 md:grid-cols-[1.5fr_0.8fr_1fr_auto] gap-2">
                  <Select value={row.packing_material_item_id || undefined} onValueChange={(value) => updatePackingMaterialRow(index, 'packing_material_item_id', value)}>
                    <SelectTrigger className="border-[#E5E5E5]">
                      <SelectValue placeholder="Select packing material" />
                    </SelectTrigger>
                    <SelectContent>
                      {packingMaterialItems.map((item) => (
                        <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input value={row.qty_required} onChange={(e) => updatePackingMaterialRow(index, 'qty_required', e.target.value)} placeholder="Qty" type="number" className="border-[#E5E5E5]" />
                  <Input value={row.note} onChange={(e) => updatePackingMaterialRow(index, 'note', e.target.value)} placeholder="Note" className="border-[#E5E5E5]" />
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="text-rose-700"
                    onClick={() => setForm((prev) => ({ ...prev, planned_packing_material_inputs: prev.planned_packing_material_inputs.filter((_, rowIndex) => rowIndex !== index) || [blankPackingMaterialRow()] }))}
                    disabled={form.planned_packing_material_inputs.length === 1}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))} className="border-[#E5E5E5]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#C97A5E] hover:bg-[#B06850]">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={completeModalOpen} onOpenChange={setCompleteModalOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">Complete Packing Session</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedSession ? (
              <>
                <div className="rounded-lg border border-[#E5E5E5] bg-[#FAFAF9] p-4">
                  <div className="text-sm font-medium text-[#2D2420]">Planned Finished Outputs</div>
                  <div className="mt-3 grid gap-2">
                    {(selectedSession.planned_finished_outputs || []).map((item, index) => (
                      <div key={`planned-output-${index}`} className="grid grid-cols-1 md:grid-cols-[1.6fr_0.7fr_1fr] gap-2 text-sm">
                        <div className="rounded border border-[#E5E5E5] bg-white px-3 py-2 text-[#2D2420]">{item.variant_name || item.sku || item.variant_id}</div>
                        <div className="rounded border border-[#E5E5E5] bg-white px-3 py-2 text-[#2D2420]">{item.qty_required}</div>
                        <div className="rounded border border-[#E5E5E5] bg-white px-3 py-2 text-[#A8A8A8]">{item.note || '-'}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="rounded-lg border border-[#E5E5E5] bg-[#FAFAF9] p-4">
                  <div className="text-sm font-medium text-[#2D2420]">Planned Inputs</div>
                  <div className="mt-3 grid md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs uppercase tracking-wide text-[#A8A8A8] mb-2">Raw Inputs</div>
                      <div className="space-y-2">
                        {(selectedSession.planned_raw_inputs || []).length ? (
                          selectedSession.planned_raw_inputs.map((item, index) => (
                            <div key={`planned-raw-${index}`} className="grid grid-cols-[1.6fr_0.7fr] gap-2 text-sm">
                              <div className="rounded border border-[#E5E5E5] bg-white px-3 py-2 text-[#2D2420]">{item.raw_item_name || item.raw_item_id}</div>
                              <div className="rounded border border-[#E5E5E5] bg-white px-3 py-2 text-[#2D2420]">{item.kg_required} kg</div>
                            </div>
                          ))
                        ) : (
                          <div className="text-sm text-[#A8A8A8]">No planned raw inputs.</div>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wide text-[#A8A8A8] mb-2">Packing Materials</div>
                      <div className="space-y-2">
                        {(selectedSession.planned_packing_material_inputs || []).length ? (
                          selectedSession.planned_packing_material_inputs.map((item, index) => (
                            <div key={`planned-pack-${index}`} className="grid grid-cols-[1.6fr_0.7fr] gap-2 text-sm">
                              <div className="rounded border border-[#E5E5E5] bg-white px-3 py-2 text-[#2D2420]">{item.packing_material_item_name || item.packing_material_item_id}</div>
                              <div className="rounded border border-[#E5E5E5] bg-white px-3 py-2 text-[#2D2420]">{item.qty_required} {item.unit || ''}</div>
                            </div>
                          ))
                        ) : (
                          <div className="text-sm text-[#A8A8A8]">No planned packing material inputs.</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                  <div className="text-sm font-medium text-emerald-900">Prepared Stock Movement</div>
                  <p className="mt-1 text-xs text-emerald-800">
                    {selectedSession.stock_preparation_status === 'prepared'
                      ? `Moved from ${selectedSession.prep_source_warehouse_name || 'Main Warehouse'} to ${selectedSession.source_warehouse_name || 'Packing'}.`
                      : 'Stock has not been prepared yet. Complete this step before posting.'}
                  </p>
                  <div className="mt-3 grid gap-3 md:grid-cols-2">
                    <div className="space-y-1">
                      <div className="text-xs font-semibold uppercase text-emerald-800">Raw moved</div>
                      {(selectedSession.raw_transfer_snapshot || []).length ? selectedSession.raw_transfer_snapshot.map((item, index) => (
                        <div key={`raw-transfer-${index}`} className="text-xs text-emerald-900">
                          {item.raw_item_name || item.raw_item_id}: {item.kg_moved} kg
                        </div>
                      )) : <div className="text-xs text-emerald-800">No raw transfer recorded.</div>}
                    </div>
                    <div className="space-y-1">
                      <div className="text-xs font-semibold uppercase text-emerald-800">Materials moved</div>
                      {(selectedSession.packing_material_transfer_snapshot || []).length ? selectedSession.packing_material_transfer_snapshot.map((item, index) => (
                        <div key={`mat-transfer-${index}`} className="text-xs text-emerald-900">
                          {item.packing_material_item_name || item.packing_material_item_id}: {item.qty_moved} {item.unit || ''}
                        </div>
                      )) : <div className="text-xs text-emerald-800">No packing material transfer recorded.</div>}
                    </div>
                  </div>
                </div>
              </>
            ) : null}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Actual Finished Outputs</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="border-[#E5E5E5]"
                  onClick={() => setCompleteForm((prev) => ({ ...prev, actual_finished_outputs: [...prev.actual_finished_outputs, blankOutputRow()] }))}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Output
                </Button>
              </div>
              {completeForm.actual_finished_outputs.map((row, index) => (
                <div key={`complete-output-${index}`} className="grid grid-cols-1 md:grid-cols-[1.4fr_0.7fr_1.2fr_1fr_auto] gap-2">
                  <Select value={row.variant_id || undefined} onValueChange={(value) => updateCompleteOutputRow(index, 'variant_id', value)}>
                    <SelectTrigger className="border-[#E5E5E5]">
                      <SelectValue placeholder="Select variant" />
                    </SelectTrigger>
                    <SelectContent>
                      {variants.map((variant) => (
                        <SelectItem key={variant.id} value={variant.id}>
                          {variant.sku} {variant.master_name ? `• ${variant.master_name}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input type="number" value={row.qty_required} onChange={(e) => updateCompleteOutputRow(index, 'qty_required', e.target.value)} placeholder="Qty" className="border-[#E5E5E5]" />
                  <Select value={row.blend_item_id || undefined} onValueChange={(value) => updateCompleteOutputRow(index, 'blend_item_id', value)}>
                    <SelectTrigger className="border-[#E5E5E5]">
                      <SelectValue placeholder="Choose bulk blend if used" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableBlendItems.map((item) => (
                        <SelectItem key={item.blend_item_id} value={item.blend_item_id}>
                          {item.blend_item_name} • {Number(item.kg_total || 0).toFixed(3)} kg
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input value={row.note} onChange={(e) => updateCompleteOutputRow(index, 'note', e.target.value)} placeholder="Note" className="border-[#E5E5E5]" />
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="text-rose-700"
                    onClick={() => setCompleteForm((prev) => ({ ...prev, actual_finished_outputs: prev.actual_finished_outputs.filter((_, rowIndex) => rowIndex !== index) || [blankOutputRow()] }))}
                    disabled={completeForm.actual_finished_outputs.length === 1}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                Choose a bulk blend item only for outputs made from blended stock. Leave it empty for direct raw packing outputs.
              </div>
              <div className="rounded-md border border-[#E5E5E5] bg-[#FAFAF9] px-3 py-2 text-xs text-[#5C4033]">
                If the same finished variant is produced partly from raw stock and partly from blended bulk stock, add two rows for that same variant:
                one row with the bulk blend selected, and one row without a blend item.
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Actual Raw Inputs</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="border-[#E5E5E5]"
                  onClick={() => setCompleteForm((prev) => ({ ...prev, actual_raw_inputs: [...prev.actual_raw_inputs, blankRawRow()] }))}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Raw
                </Button>
              </div>
              {completeForm.actual_raw_inputs.map((row, index) => (
                <div key={`complete-raw-${index}`} className="grid grid-cols-1 md:grid-cols-[1.5fr_0.8fr_1fr_auto] gap-2">
                  <Select value={row.raw_item_id || undefined} onValueChange={(value) => updateCompleteRawRow(index, 'raw_item_id', value)}>
                    <SelectTrigger className="border-[#E5E5E5]">
                      <SelectValue placeholder="Select raw item" />
                    </SelectTrigger>
                    <SelectContent>
                      {rawItems.map((item) => (
                        <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input type="number" value={row.kg_required} onChange={(e) => updateCompleteRawRow(index, 'kg_required', e.target.value)} placeholder="KG" className="border-[#E5E5E5]" />
                  <Input value={row.note} onChange={(e) => updateCompleteRawRow(index, 'note', e.target.value)} placeholder="Note" className="border-[#E5E5E5]" />
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="text-rose-700"
                    onClick={() => setCompleteForm((prev) => ({ ...prev, actual_raw_inputs: prev.actual_raw_inputs.filter((_, rowIndex) => rowIndex !== index) || [blankRawRow()] }))}
                    disabled={completeForm.actual_raw_inputs.length === 1}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Actual Packing Materials Used</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="border-[#E5E5E5]"
                  onClick={() => setCompleteForm((prev) => ({ ...prev, actual_packing_material_inputs: [...prev.actual_packing_material_inputs, blankPackingMaterialRow()] }))}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Material
                </Button>
              </div>
              {completeForm.actual_packing_material_inputs.map((row, index) => (
                <div key={`complete-material-${index}`} className="grid grid-cols-1 md:grid-cols-[1.5fr_0.8fr_1fr_auto] gap-2">
                  <Select value={row.packing_material_item_id || undefined} onValueChange={(value) => updateCompleteMaterialRow(index, 'packing_material_item_id', value)}>
                    <SelectTrigger className="border-[#E5E5E5]">
                      <SelectValue placeholder="Select material" />
                    </SelectTrigger>
                    <SelectContent>
                      {packingMaterialItems.map((item) => (
                        <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input type="number" value={row.qty_required} onChange={(e) => updateCompleteMaterialRow(index, 'qty_required', e.target.value)} placeholder="Qty" className="border-[#E5E5E5]" />
                  <Input value={row.note} onChange={(e) => updateCompleteMaterialRow(index, 'note', e.target.value)} placeholder="Note" className="border-[#E5E5E5]" />
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="text-rose-700"
                    onClick={() => setCompleteForm((prev) => ({ ...prev, actual_packing_material_inputs: prev.actual_packing_material_inputs.filter((_, rowIndex) => rowIndex !== index) || [blankPackingMaterialRow()] }))}
                    disabled={completeForm.actual_packing_material_inputs.length === 1}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="space-y-3">
              <Label>Raw Wastage</Label>
              {completeForm.raw_wastage_inputs.map((row, index) => (
                <div key={`raw-wastage-${index}`} className="grid grid-cols-1 md:grid-cols-[1.5fr_0.8fr_1fr] gap-2">
                  <Select value={row.raw_item_id || undefined} onValueChange={(value) => updateRawWastageRow(index, 'raw_item_id', value)}>
                    <SelectTrigger className="border-[#E5E5E5]">
                      <SelectValue placeholder="Select raw item" />
                    </SelectTrigger>
                    <SelectContent>
                      {rawItems.map((item) => (
                        <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input type="number" value={row.wastage_kg} onChange={(e) => updateRawWastageRow(index, 'wastage_kg', e.target.value)} placeholder="Wastage KG" className="border-[#E5E5E5]" />
                  <Input value={row.note} onChange={(e) => updateRawWastageRow(index, 'note', e.target.value)} placeholder="Reason / note" className="border-[#E5E5E5]" />
                </div>
              ))}
            </div>
            <div className="space-y-3">
              <Label>Packing Material Wastage</Label>
              {completeForm.packing_material_wastage_inputs.map((row, index) => (
                <div key={`material-wastage-${index}`} className="grid grid-cols-1 md:grid-cols-[1.5fr_0.8fr_1fr] gap-2">
                  <Select value={row.packing_material_item_id || undefined} onValueChange={(value) => updateMaterialWastageRow(index, 'packing_material_item_id', value)}>
                    <SelectTrigger className="border-[#E5E5E5]">
                      <SelectValue placeholder="Select material" />
                    </SelectTrigger>
                    <SelectContent>
                      {packingMaterialItems.map((item) => (
                        <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input type="number" value={row.wastage_qty} onChange={(e) => updateMaterialWastageRow(index, 'wastage_qty', e.target.value)} placeholder="Wastage Qty" className="border-[#E5E5E5]" />
                  <Input value={row.note} onChange={(e) => updateMaterialWastageRow(index, 'note', e.target.value)} placeholder="Reason / note" className="border-[#E5E5E5]" />
                </div>
              ))}
            </div>
            <div>
              <Label>Production Loss (KG)</Label>
              <Input type="number" value={completeForm.production_loss_kg} onChange={(e) => setCompleteForm((prev) => ({ ...prev, production_loss_kg: e.target.value }))} className="border-[#E5E5E5]" />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={completeForm.notes} onChange={(e) => setCompleteForm((prev) => ({ ...prev, notes: e.target.value }))} className="border-[#E5E5E5]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteModalOpen(false)}>Cancel</Button>
            <Button onClick={handleComplete} disabled={saving} className="bg-[#C97A5E] hover:bg-[#B06850]">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Complete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">Sticker Preview</DialogTitle>
          </DialogHeader>
          {previewLoading ? (
            <div className="flex items-center justify-center h-48">
              <Loader2 className="w-8 h-8 animate-spin text-[#C97A5E]" />
            </div>
          ) : previewData ? (
            <div className="space-y-4">
              <div className="rounded-lg border border-[#E5E5E5] bg-[#FAFAF9] p-4">
                <div className="text-sm text-[#5C4033]">Batch Code: {previewData.batch_code || '-'}</div>
                <div className="text-sm text-[#5C4033]">Packing Date: {previewData.packing_date || '-'}</div>
                <div className="text-sm text-[#5C4033]">Total Stickers: {previewData.sticker_summary?.total_stickers || 0}</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(previewData.items || []).map((item) => (
                  <div key={item.variant_id} className="rounded-lg border border-[#E5E5E5] p-4 space-y-3 bg-white">
                    <div>
                      <div className="font-medium text-[#2D2420]">{item.variant_name}</div>
                      <div className="text-sm text-[#A8A8A8]">
                        {item.weight_label} • MRP Rs. {Number(item.retail_mrp || 0).toFixed(2)} • Stickers {item.sticker_count}
                      </div>
                    </div>
                    <div className="rounded-lg bg-[#FAFAF9] p-3">
                      <img src={item.image_data_url} alt={item.variant_name} className="w-full h-auto rounded object-contain" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-sm text-[#A8A8A8]">No preview available.</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
